import {useContext, useEffect} from 'react';

import {useTranslation} from 'react-i18next';
import {Navigate, Outlet} from 'react-router-dom';

import {SnackBarContext} from '../../contexts/SnackBarContext/SnackBarContext';
import {UserContext} from '../../contexts/UserContext/UserContext';
import {ApplicantOnboarding} from '../../features/applicant/onboarding/types';
import {useApiRead} from '../../hooks/useApiRead';
import {GetRoutes} from '../../lib/api/GET/GetRoutes';
import {AppConfig} from '../../lib/AppConfig';
import {ApplicantRoleData} from '../../types/User';

export const ApplicantOnboardingCompletedGuard = () => {
  const {currentUser, isLoading} = useContext(UserContext);
  const {t} = useTranslation();
  const applicantId = (currentUser?.role.data as ApplicantRoleData)?.applicantId;
  const {dispatchMessage} = useContext(SnackBarContext);
  const {
    data,
    isPending,
    isLoading: isLoadingOnboarding,
    refetch,
  } = useApiRead<{applicantOnboarding: ApplicantOnboarding}>({
    path: GetRoutes.applicantOnboarding(applicantId),
    options: {enabled: false},
  });

  const isAnyLoading = isLoading || isPending || isLoadingOnboarding;

  useEffect(() => {
    if (applicantId) {
      refetch();
    }
  }, [applicantId, refetch]);

  useEffect(() => {
    if (!data?.applicantOnboarding?.completedAt && !isAnyLoading) {
      dispatchMessage(t('applicantOnboarding.popup'), 'info');
    }
  }, [data?.applicantOnboarding?.completedAt, dispatchMessage, isAnyLoading, t]);

  if (isAnyLoading) {
    return null;
  }

  if (!applicantId) {
    // This case should not occur at this stage, but just in case we navigate back to home.
    return <Navigate to={'/'} />;
  }

  if (!data) {
    return null;
  }

  return data?.applicantOnboarding?.completedAt ? <Outlet /> : <Navigate to={AppConfig.APPLICANT_ONBOARDING_PATH} />;
};
